<script>
  import { differenceInCalendarDays } from 'date-fns'
  import { asText } from '@prismicio/client'
  import * as Duration from 'duration-fns'
  import { page } from '$app/stores'

  import { i18n as countryNames } from '$lib/countries.js'
  import { resolveLang, resolve } from '$lib/prismic.js'
  import Header, { LANGUAGES } from '$lib/Header.svelte'
  import { getCheapestPrice } from '$lib/Journey.svelte'
  import { money } from '$lib/utils/money.svelte.js'
  import PageHeading from '$lib/PageHeading.svelte'
  import { track } from '$lib/plausible.svelte.js'
  import { text, register } from '$lib/text.js'
  import { getLanguage } from '$lib/context.js'
  import RichText from '$lib/RichText.svelte'
  import { setMeta } from '$lib/Meta.svelte'
  import Symbol from '$lib/Symbol.svelte'
  import Footer from '$lib/Footer.svelte'
  import Figure from '$lib/Figure.svelte'
  import Loader from '$lib/Loader.svelte'
  import Button from '$lib/Button.svelte'
  import Image from '$lib/Image.svelte'
  import { i18n } from '../../i18n.js'
  import Hero from '$lib/Hero.svelte'
  import Html from '$lib/Html.svelte'
  import Map from '$lib/Map.svelte'

  register(i18n, countryNames)

  const language = getLanguage()

  const schema = (data) =>
    `<% type="application/ld+json">${JSON.stringify(data)}</%>`.replace(
      /(?:^(<)%)|(?:%(>))$/,
      '$1script$2'
    )

  const stringToFixedDecimal = (str) => {
    let hash = 0

    // Convert the string to a hash code
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash |= 0 // Convert to 32bit integer
    }

    // Convert the hash code to a decimal between 0 and 1
    // The maximum value for a 32bit integer is 2^31 - 1, so we use 2^32 for normalization
    const decimal = (hash >>> 0) / Math.pow(2, 32) // >>> 0 ensures the result is an unsigned 32-bit integer

    return decimal
  }

  const getDefaultDescription = (opts = {}, key) => {
    const { asHtml = false } = opts
    const format = (value) =>
      asHtml ? `<strong class="u-nowrap">${value}</strong>` : value

    const duration = format($text`${days} days`)
    const names = locations.map((location) => location.name)
    const viaList = conjunction.format(names.slice(1, -1).map(format))
    const pairs = hasNightTrain
      ? [
          [
            $text`Traveling from ${format(originName)} to ${format(destinationName)} by night train is the fastest and most comfortable option.`,
            $text`Taking the night train from ${format(originName)} to ${format(destinationName)} is a great and comfortable choice.`,
            $text`Go by night train from ${format(originName)} to ${format(destinationName)} and arrive rested after a good night's sleep.`
          ],
          [
            $text`It's a ${duration} train journey, taking you quickly to your destination, ${viaList ? $text`stopping by ${viaList}` : $text`arriving well rested`}.`,
            $text`You will ${viaList ? $text`pass through ${viaList}` : $text`get a good nights rest`} before reaching your destination on this ${duration} long train journey.`,
            $text`This train route takes ${duration} and you will ${viaList ? $text`pass ${viaList}` : $text`get a good nights rest`} on the way to your destination.`
          ]
        ]
      : [
          [
            $text`Traveling from ${format(originName)} to ${format(destinationName)} by train is a great experience.`,
            $text`Traveling by train from ${format(originName)} to ${format(destinationName)} is an unforgettable journey.`,
            $text`The trip from ${format(originName)} to ${format(destinationName)} by train is fantastic.`
          ],
          [
            $text`It's a ${duration} train journey with amazing scenery, stopping by ${viaList} on the way.`,
            $text`You'll pass through ${viaList}. It's a ${duration} long train ride with beautiful scenery.`,
            $text`The journey takes ${duration}, through beautiful landscapes, stopping in ${viaList} on the way.`
          ]
        ]

    const firstIndex = stringToFixedDecimal(names.join(''))
    const secondIndex = stringToFixedDecimal([...names].reverse().join(''))

    return [
      pairs[0][Math.floor(3 * firstIndex)],
      pairs[1][Math.floor(3 * secondIndex)]
    ].join(' ')
  }

  const getDay = (items) => {
    let day = 1
    for (const item of items) {
      if ('location' in item) {
        day += Math.floor(Duration.toDays(item.duration))
      }
      if ('segment' in item) {
        day += differenceInCalendarDays(
          new Date(item.segment.arrivalAt),
          new Date(item.segment.departureAt)
        )
      }
    }
    return day
  }

  const getTranslation = (doc) =>
    data.translations.find((translation) =>
      translation.alternate_languages.some((lang) => lang.id === doc.id)
    ) || doc

  export let data

  $: variant = data.route.data.variants.find(
    (slice, index) =>
      slice.slice_type === 'route_variant' &&
      ((!$page.params.blueprint && !index) ||
        slice.primary.itinerary_blueprint?.id === $page.params.blueprint)
  )

  $: conjunction = new Intl.ListFormat($language, {
    style: 'long',
    type: 'conjunction'
  })

  $: disjunction = new Intl.ListFormat($language, {
    style: 'long',
    type: 'disjunction'
  })

  $: origin = getTranslation(data.origin)
  $: destination = getTranslation(data.destination)
  $: originName = asText(origin.data.name)
  $: destinationName = asText(destination.data.name)
  $: days = data.blueprint.itinerary.reduce((acc, item, index, list) => {
    switch (item.__typename) {
      case 'EditSearchPredicate':
        return index === list.length - 2
          ? acc // Don't count trailing night train as a travel day
          : acc + (item.offsetDays || 0)
      case 'EditStopover':
        return acc + Math.floor(Duration.toDays(item.duration))
      default:
        return acc
    }
  }, 1)
  $: locations = data.blueprint.itinerary
    .filter((item) => item.__typename === 'EditStopover')
    .map((/** @type {Schema.EditStopover} */ item) => item.location)
  $: hasNightTrain = data.blueprint.itinerary.some(
    (item) =>
      item.__typename === 'EditSearchPredicate' &&
      item.transports?.includes(/** @type {Schema.Transport} */ ('NIGHT_TRAIN'))
  )
  $: suffix = $page.params.blueprint ? `/${$page.params.blueprint}` : ''
  $: variantDescription = asText(variant.primary.description)
  $: routeDescription = asText(data.route.data.description)
  $: descriptionAsText =
    variantDescription ||
    routeDescription ||
    getDefaultDescription({}, $page.url)
  $: image = variant.primary.image
  $: languages = [
    { href: `/routes/${data.route.uid + suffix}`, lang: 'en-gb' },
    { href: `/de/routes/${data.route.uid + suffix}`, lang: 'de-de' },
    { href: `/fr/routes/${data.route.uid + suffix}`, lang: 'fr-fr' },
    { href: `/es/routes/${data.route.uid + suffix}`, lang: 'es-es' },
    { href: `/sv/routes/${data.route.uid + suffix}`, lang: 'sv-se' }
  ]

  /** @type {Schema.Money?} */
  let price = null
  $: data.journeyOffer?.then(
    (journeyOffer) => {
      // eslint-disable-next-line svelte/infinite-reactive-loop
      price = getCheapestPrice(journeyOffer)
    },
    () => {
      // eslint-disable-next-line svelte/infinite-reactive-loop
      price = null
    }
  )

  $: setMeta({
    title:
      (hasNightTrain
        ? $text`${originName} to ${destinationName} by night train`
        : $text`${originName} to ${destinationName} by train`) +
      (price ? ` ${$text`from ${money(price)}`}` : ''),
    description: descriptionAsText,
    image
  })
</script>

<svelte:head>
  <link rel="canonical" href={data.canonical} />
</svelte:head>

<Header adaptive {languages} />

<Hero>
  <Figure slot="background" {image} fill loading="eager" />
  <PageHeading>
    <div slot="tagline">
      <p>{$text`A step-by-step travel guide`}</p>
    </div>
    <div slot="promotion">
      <h1 style="text-wrap: balance">
        {#if hasNightTrain}
          {@html $text`Take the night train from ${`<strong>${originName}</strong>`} to ${`<strong>${destinationName}</strong>`}`}
        {:else}
          {@html $text`Take the train from ${`<strong>${originName}</strong>`} to ${`<strong>${destinationName}</strong>`}`}
        {/if}
      </h1>
    </div>
  </PageHeading>
</Hero>

<div class="u-container timeline">
  <div class="description">
    <div class="map">
      {#await data.journey then journey}
        {#if journey}
          <Map
            locations={data.blueprint.itinerary
              .filter((item) => item.__typename === 'EditStopover')
              .map((/** @type {Schema.EditStopover} */ item) => item.location)}
            polylines={journey.itinerary
              ?.map(
                (/** @type {Schema.SegmentCollection} */ item) => item.polylines
              )
              .filter(Boolean) || []} />
        {/if}
      {/await}
    </div>
    <Html>
      {#if variantDescription}
        <RichText content={variant.primary.description} />
      {:else if routeDescription}
        <RichText content={data.route.data.description} />
      {:else}
        <p>{@html getDefaultDescription({ asHtml: true }, $page.url)}</p>
      {/if}
    </Html>
    <div class="cta">
      {#await data.journeyOffer}
        <div class="price">
          {$text`Price fr.`}
          <strong class="amount"><Loader length={2} /></strong>
        </div>
      {:then journeyOffer}
        {@const price = getCheapestPrice(journeyOffer)}
        {#if price}
          <div class="price">
            {$text`Price fr.`}
            <strong class="amount">{money(price)}</strong>
          </div>
        {/if}
      {/await}
      <Button
        size="lg"
        onclick={() =>
          track('Select route', {
            props: { blueprint: variant.primary.itinerary_blueprint?.id }
          })}
        href="/search?origin={data.origin.data.location.uid}&destination={data
          .destination.data.location
          .uid}&ddate={data.departureDate}&blueprint={variant.primary
          .itinerary_blueprint?.id || ''}">
        <Symbol name="magnifier" prefix />
        {$text`Find tickets`}
      </Button>
    </div>
  </div>
  <ol class="itinerary">
    <li class="day theme-yellow">
      {$text`Day ${String(1)}`}
    </li>
    {#await data.journey}
      <li class="journey">
        <div>
          <span class="row">
            <span class="time"><Loader length={2} /></span>
            <span class="location"><Loader length={5} /></span>
          </span>
          <span class="row">
            <span class="time"><Loader length={2} /></span>
            <span class="location"><Loader length={5} /></span>
          </span>
        </div>
      </li>
      <li class="stopover">
        <div class="thumbnail"></div>
        <div class="destination">
          <Loader length={12} />
        </div>
      </li>
    {:then journey}
      {#if journey?.status === 'SUCCESS' && journey.itinerary}
        {@const items = journey.itinerary.flatMap(
          /** @type {function(Schema.ItineraryItem): Schema.Stopover|{ segment: Schema.Segment}|Array} */ (
            item
          ) => {
            switch (item.__typename) {
              case 'SegmentCollection':
                return item.segments?.map((segment) => ({ segment })) || []
              case 'Stopover':
                return item
              default:
                return []
            }
          }
        )}
        {#each items as item, index}
          {#if 'segment' in item}
            {@const prev = items[index - 1]}
            {@const departureAt = new Date(item.segment.departureAt)}
            {@const arrivalAt = new Date(item.segment.arrivalAt)}
            {@const diff =
              prev?.segment &&
              differenceInCalendarDays(
                departureAt,
                new Date(prev.segment.departureAt)
              )}
            {#if diff}
              <li class="day theme-yellow">
                {$text`Day ${String(getDay(items.slice(0, index)))}`}
              </li>
            {/if}
            <li
              class="segment"
              class:night-train={item.segment.transport === 'NIGHT_TRAIN'}>
              <div>
                <span class="row">
                  <span class="time">
                    {departureAt.toLocaleString($language, {
                      hour: '2-digit',
                      minute: '2-digit',
                      hourCycle: 'h23'
                    })}
                  </span>
                  <span class="location">
                    <strong>{item.segment.origin.name}</strong>
                    {#if 'countryCode' in item.segment.origin}
                      {$text(item.segment.origin.countryCode)}
                    {/if}
                  </span>
                </span>
                <span class="row">
                  <span class="time">
                    {arrivalAt.toLocaleString($language, {
                      hour: '2-digit',
                      minute: '2-digit',
                      hourCycle: 'h23'
                    })}
                  </span>
                  <span class="location">
                    <span class="name">
                      <strong>{item.segment.destination.name}</strong>
                      {#if 'countryCode' in item.segment.destination}
                        {$text(item.segment.destination.countryCode)}
                      {/if}
                    </span>
                  </span>
                </span>
              </div>
              {#if item.segment.operator.image}
                <img
                  class="operator"
                  alt={item.segment.operator.name}
                  src={item.segment.operator.image.url} />
              {/if}
            </li>
          {/if}
          {#if 'location' in item}
            <li class="stopover">
              <div class="thumbnail">
                {#if item.location.image}
                  <Image
                    class="image"
                    width="1000"
                    height="1000"
                    src={item.location.image.url}
                    alt={item.location.image.alt || item.location.name}
                    sizes="10vw"
                    variants={[80, 160, 360]} />
                {:else}
                  <Symbol name="pin" size="lg" />
                {/if}
              </div>
              <div class="destination">
                {@html $text`Spend ${Math.ceil(Duration.toDays(item.duration))} nights in ${`<br><strong>${item.location.name}</strong>`}`}
              </div>
            </li>
            <li class="day theme-yellow">
              {$text`Day ${String(getDay(items.slice(0, index)) + 1)}`}
            </li>
          {/if}
        {/each}
        <li class="stopover">
          <div class="thumbnail">
            {#if destination.data.image.url}
              <Image
                class="image"
                {...destination.data.image.dimensions}
                src={destination.data.image.url}
                alt={destination.data.image.alt || destination.data.name}
                sizes="10vw"
                variants={[80, 160, 360]} />
            {:else}
              <Symbol name="pin" size="lg" />
            {/if}
          </div>
          <div class="destination">
            {@html $text`Arrive at your final destination, ${`<strong>${asText(destination.data.name)}</strong>`}.`}
          </div>
        </li>
      {/if}
    {/await}
  </ol>
</div>

<div class="sections">
  {#await data.opposite then doc}
    {#if doc}
      <div class="section">
        <hr />
      </div>

      <div class="section">
        <Html>
          <p style="margin-bottom: 1.5rem">
            {$text`Planning for the opposite direction?`}
          </p>
        </Html>
        <PageHeading>
          <div slot="promotion">
            <h2 style="text-wrap: balance">
              {@html $text`See the ${`<a class="u-link" href="${resolve(doc)}" data-sveltekit-reload>${$text`Travel from ${destinationName} to ${originName}`}</a>`} page.`}
            </h2>
          </div>
        </PageHeading>
      </div>
    {/if}
  {/await}

  {#if data.faq.length}
    <div class="section">
      <hr />
    </div>

    <div class="section">
      <Html>
        <h2>
          {$text`Good to know when traveling from ${originName} to ${destinationName}`}
        </h2>

        {#each data.faq as faq}
          {@const slices = faq.data.body.filter(
            (slice) => slice.slice_type === 'body'
          )}
          {#if slices.length}
            <RichText level={2} content={faq.data.title} />
            {#each slices as slice}
              <RichText content={slice.primary.content} />
            {/each}
          {/if}
        {/each}
      </Html>
    </div>
  {/if}

  {#if data.blueprintVariants.length}
    <div class="section">
      <Html>
        <p style="margin-bottom: 1.5rem">
          {$text`There are other ways to travel`}
        </p>
      </Html>
      <PageHeading>
        <div slot="promotion">
          <p style="text-wrap: balance">
            {@html $text`See routes from ${`<strong>${originName}</strong>`} to ${`<strong>${destinationName}</strong>`} traveling ${disjunction.format(
              data.blueprintVariants.map((blueprint) => {
                const stopovers = blueprint.itinerary
                  .filter((item) => item.__typename === 'EditStopover')
                  .slice(1, -1)
                const predicates = blueprint.itinerary.filter(
                  (item) => item.__typename === 'EditSearchPredicate'
                )
                const feature = predicates.some((item) =>
                  item.transports?.includes('NIGHT_TRAIN')
                )
                  ? $text`by night train`
                  : stopovers.length
                    ? $text`via ${stopovers[0].location.name}`
                    : $text`direct`
                return `<a href="${$page.params.lang ? `/${$page.params.lang}` : ''}/routes/${$page.params.route}/${blueprint.id}" class="u-link">${feature}</a>`
              })
            )}.`}
          </p>
        </div>
      </PageHeading>
    </div>
  {/if}

  <div class="section">
    <div class="groups">
      {#await data.similar then { origin, destination, popular }}
        {@const cols = [
          {
            title: $text`Popular trips from ${originName}`,
            items: origin.results
          },
          {
            title: $text`Popular trips from ${destinationName}`,
            items: destination.results
          },
          {
            title: $text`Popular train tips`,
            items: popular.results
          }
        ]}
        {#each cols as { title, items }, index}
          {#if items.length}
            {@const src = items[0].data.variants[0].primary.image.url}
            {@const origin = getTranslation(items[0].data.origin)}
            {@const destination = getTranslation(items[0].data.destination)}
            {@const originName = asText(origin.data.name)}
            {@const destinationName = asText(destination.data.name)}
            <div class="group">
              <a class="card" href={resolve(items[0])}>
                <Image
                  {src}
                  {...items[0].data.variants[0].primary.image.dimensions}
                  class="image"
                  loading="lazy"
                  alt={asText(items[0].data.name) || ''}
                  variants={[300, 600, 900, 1200]} />
                <span class="label">{$text`Popular`}</span>
                <div class="about">
                  <h3 class="title">
                    {#if index % 4 === 2}
                      {$text`Trains from ${originName} to ${destinationName}`}
                    {:else if index % 4 === 1}
                      {$text`Travel from ${originName} to ${destinationName}`}
                    {:else}
                      {$text`${originName} to ${destinationName} by train`}
                    {/if}
                  </h3>
                  <span>
                    {#if index % 3 === 2}
                      {$text`Learn how to best travel from ${originName} to ${destinationName} by train.`}
                    {:else if index % 3 === 1}
                      {$text`Get the best train route to ${destinationName} from ${originName}.`}
                    {:else}
                      {$text`Go from ${originName} to ${destinationName} in ${items[0].data.variants[0].items.length} simple steps.`}
                    {/if}
                  </span>
                  <span class="target">{$text`See route`}</span>
                </div>
              </a>
              {#if items.length > 1}
                <Html>
                  <h3>{title}</h3>
                </Html>
                <ol class="items">
                  {#each items.slice(1) as doc, _index}
                    {@const origin = getTranslation(doc.data.origin)}
                    {@const destination = getTranslation(doc.data.destination)}
                    {@const originName = `<strong>${asText(origin.data.name)}</strong>`}
                    {@const destinationName = `<strong>${asText(
                      destination.data.name
                    )}</strong>`}
                    <li class="item">
                      <a href={resolve(doc)} class="u-link">
                        {#if (_index + index) % 4 === 3}
                          {@html $text`${originName} to ${destinationName} by train`}
                        {:else if (_index + index) % 4 === 2}
                          {@html $text`Trains from ${originName} to ${destinationName}`}
                        {:else if (_index + index) % 4 === 1}
                          {@html $text`${originName} to ${destinationName}`}
                        {:else}
                          {@html $text`Travel from ${originName} to ${destinationName}`}
                        {/if}
                      </a>
                    </li>
                  {/each}
                </ol>
              {/if}
            </div>
          {/if}
        {/each}
      {/await}
    </div>
  </div>

  <div class="section gray">
    <Html>
      <p style="margin-bottom: 1.5rem">{$text`Available in other languages`}</p>
    </Html>
    <PageHeading>
      <div slot="promotion">
        <p style="text-wrap: balance">
          {@html $text`See the travel guide in ${conjunction.format(
            languages
              .filter(({ lang }) => resolveLang($language) !== lang)
              .map(
                ({ href, lang }) =>
                  `<a href="${href}" class="u-link" hreflang="${lang.substring(0, 2)}">${LANGUAGES[lang]}</a>`
              )
          )}.`}
        </p>
      </div>
    </PageHeading>
  </div>

  <div class="section gray">
    <hr />
  </div>
</div>

<Footer />

{#await data.journey then journey}
  {#if journey?.status === 'SUCCESS'}
    {@const segments = journey.itinerary
      ?.filter((item) => item.__typename === 'SegmentCollection')
      .flatMap((/** @type {Schema.SegmentCollection} */ item) => item.segments)
      .filter(Boolean)}
    {@const stopovers = journey.itinerary?.filter(
      (item) => item.__typename === 'Stopover'
    )}
    {@const firstLeg = segments?.[0]}
    {@const lastLeg = segments?.[segments.length - 1]}
    {@html schema({
      '@context': 'https://schema.org',
      '@type': 'TrainTrip',
      potentialAction: {
        '@context': 'https://schema.org',
        '@type': 'TravelAction'
      }
    })}
    {@html schema([
      {
        '@context': 'https://schema.org',
        '@type': 'TrainTrip',
        departureStation: {
          '@type': 'TrainStation',
          name: firstLeg?.origin.name
        },
        departureTime: firstLeg?.departureAt,
        arrivalStation: {
          '@type': 'TrainStation',
          name: lastLeg?.destination.name
        },
        arrivalTime: firstLeg?.arrivalAt,
        subTrip: segments?.map((segment) => ({
          '@type': 'TrainTrip',
          departureStation: {
            '@type': 'TrainStation',
            name: segment.origin.name
          },
          arrivalStation: {
            '@type': 'TrainStation',
            name: segment.destination.name
          },
          departureTime: segment.departureAt,
          arrivalTime: segment.arrivalAt,
          provider: {
            '@type': 'Organization',
            name: segment.operator.name
          }
        })),
        itinerary: stopovers?.map(
          (/** @type {Schema.Stopover} */ stopover) => ({
            '@type': 'Place',
            name: stopover.location.name,
            description: $text`Spend ${Math.ceil(Duration.toDays(stopover.duration))} nights in ${stopover.location.name}`
          })
        ),
        offers: price
          ? {
              '@type': 'Offer',
              availability: 'https://schema.org/InStock',
              price: price.amount / 100,
              priceCurrency: price.currency
            }
          : undefined
      }
    ])}
  {/if}
{/await}

<style>
  .sections {
    --item-padding: 5rem;
    --item-padding-sm: 2rem;
    --current-background: var(--background);

    position: relative;
  }

  @media (min-width: 600px) {
    .sections {
      --item-padding: 6rem;
    }
  }

  @media (min-width: 1000px) {
    .sections {
      --item-padding-sm: 3.5rem;
      --item-padding: 8rem;
    }
  }

  .section {
    --gutter: max(
      calc((100vw - var(--container-large-width)) / 2),
      var(--document-margin)
    );

    width: 100%;
    padding: var(--item-padding) var(--gutter);
  }

  @supports (padding-left: env(safe-area-inset-left)) {
    .section {
      padding-left: calc(var(--gutter) + env(safe-area-inset-left));
      padding-right: calc(var(--gutter) + env(safe-area-inset-right));
    }
  }

  .section:empty {
    display: none;
    padding: 0;
  }

  .gray {
    --current-background: rgb(var(--rgb-bright-gray));

    background: var(--current-background);
  }

  .section + :global(.section) {
    /* Not sure if this one is in use */
    padding-top: 0 !important;
  }

  /* Make sure we get a double margin between items with different backgrounds */

  .gray + :global(.section:not(.gray)),
  .section:not(.gray) + :global(.gray) {
    padding-top: var(--item-padding) !important;
  }

  /* Handle dividers with gray above and below */

  .gray + .section:has(hr) {
    padding-top: 0 !important;
  }

  .section:has(hr):last-child {
    padding-bottom: 0 !important;
  }

  /* Timeline */

  .timeline {
    --gap: 6rem;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--gap);
  }

  @media (width >= 1000px) {
    .timeline {
      --gap: clamp(2rem, 5vw, 7rem);
      flex-direction: row;
    }
  }

  /*  Description */

  .description {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2rem;
    width: 100%;
    padding-top: 3rem;
  }

  @media (width >= 1000px) {
    .description {
      width: calc(50% - var(--gap) - 6rem);
      position: sticky;
      top: 0;
    }
  }

  .cta {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    justify-content: space-between;
    width: 100%;
    font-size: 1.125rem;
  }

  .price {
    line-height: 1.25;
  }

  .amount {
    display: block;
    font-size: 2rem;
  }

  .map {
    max-height: 30vh;
    width: 100%;
    position: relative;
    border-radius: var(--border-radius-md);
    overflow: hidden;
  }

  .map::after {
    content: '';
    display: block;
    padding-bottom: 133%;
  }

  /* Itinerary */

  .itinerary {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: clamp(3rem, 6vw, 6rem);
    padding-left: 6rem;

    background-image: radial-gradient(
      circle,
      rgb(var(--rgb-muted), 0.4) 1px,
      transparent 1px
    );
    background-size: 6px 6px; /* 2px for the circle diameter + 4px for the spacing */
    background-position:
      calc(6rem - 3px) 50%,
      3px 50%; /* Position the dots in the middle of the line */
    background-repeat: repeat-y;
  }

  /* Day */

  .day {
    font-size: 1.5rem;
    font-weight: 600;
    background: var(--background);
    color: rgb(var(--rgb-text));
    padding: 0.75rem 1.75rem;
    border-radius: var(--border-radius-full);
    transform: translateX(-50%);
  }

  .day:first-child {
    margin: -1.75rem 0 0;
  }

  /* Journey */

  .segment {
    position: relative;
    font-size: clamp(1rem, 1.5vw, 1.5rem);
  }

  .row {
    display: flex;
    position: relative;
    padding-left: 1.5em;
    white-space: nowrap;
  }

  .row::before {
    content: '';
    width: 1.5em;
    height: 100%;

    position: absolute;
    inset: 0;
    left: 0;
    top: 0;
    transform: translateX(-50%);

    background-color: rgb(var(--rgb-red));
  }

  .segment.night-train .row::before {
    background-color: rgb(var(--rgb-blue));
  }

  .row:first-child::before {
    border-radius: 1.5rem 1.5rem 0 0;
  }

  .row:last-child::before {
    border-radius: 0 0 1.5rem 1.5rem;
  }

  .time {
    padding: 0.25em 0;
    position: absolute;
    right: calc(100% + 1.5em);
  }

  .location {
    padding: 0.25em 0;
    position: relative;
  }

  .location::before {
    content: '';
    width: 0.5em;
    height: 0.5em;

    position: absolute;
    left: -1.75em;
    top: calc(50% - 0.25em);

    background: var(--background);
    border-radius: 100%;
  }

  .operator {
    width: auto;
    height: clamp(1.25rem, 2.5vw, 2rem);
    position: absolute;
    bottom: calc(100% + 0.25em);
    left: 2em;
  }

  /* Stopover */

  .stopover {
    display: flex;
    align-items: center;
    position: relative;
    padding-left: calc(clamp(3rem, 10vw, 6rem) / 2 + 1rem);
  }

  .thumbnail {
    --current-color: rgb(var(--rgb-muted));

    width: clamp(3rem, 10vw, 6rem);
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1;
    overflow: hidden;
    position: absolute;
    right: 100%;
    transform: translateX(50%);
    background: var(--background-alt);
    border-radius: var(--border-radius-sm);
  }

  .thumbnail :global(.image) {
    object-fit: cover;
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
  }

  .destination {
    max-width: 12em;
    font-size: clamp(1rem, 1.5vw, 1.5rem);
    line-height: 1.3;
  }

  /* Similar */

  .groups {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
    gap: 3.5rem 2.5rem;
    width: 100%;
  }

  .group {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  /* Card */

  .card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    aspect-ratio: 0.8;
    padding: 1.5rem;
    max-height: 35rem;
    position: relative;
    z-index: 0;
    overflow: hidden;
    color: rgb(var(--rgb-white));
    border-radius: var(--border-radius-lg);
  }

  .card::before {
    content: '';
    display: block;
    width: calc(100% + 2rem);
    height: calc(100% + 2rem);
    margin: -1rem 0 0 -1rem;
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.2) 50%,
      rgba(0, 0, 0, 0.8) 100%
    );
  }

  .card :global(.image) {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    z-index: -2;
    object-fit: cover;
  }

  .label {
    font-size: 1rem;
    font-weight: 600;
  }

  .about {
    display: grid;
    gap: 0.75rem;
    text-wrap: balance;
    transform: translateY(2.15rem);
    transition: transform 250ms cubic-bezier(0.33, 1, 0.68, 1);
  }

  .card:hover .about {
    transform: translateY(0rem);
  }

  .title {
    font-family: var(--heading-font-family);
    font-weight: bold;
    font-size: 1.5rem;
    line-height: 1.15;
    word-spacing: -0.05em;
    letter-spacing: -0.02em;
    text-wrap: balance;
    max-width: 70%;
  }

  @media (min-width: 900px) {
    .title {
      font-size: 1.6rem;
      line-height: 1.1;
    }
  }

  .target {
    color: currentColor;
    font-weight: 600;
    text-decoration: none;
    opacity: 0;
    transition: opacity 250ms cubic-bezier(0.33, 1, 0.68, 1);
  }

  .card:hover .target {
    opacity: 1;
  }

  .target::after {
    content: '';
    inset: 0;
    position: absolute;
    left: 0;
    top: 0;
  }

  /* Items */

  .items {
    --rgb-link: var(--rgb-dark-gray);

    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
    font-size: 1rem;
  }
</style>
